import React from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Chip,
} from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import { useDeploymentMap } from '../hooks/useDeploymentMap';

const getVersionColor = (version: string | undefined): 'success' | 'warning' | 'error' | 'default' => {
  if (!version || version === 'n/a') return 'default';
  return 'success';
};

const DeploymentMapPanel: React.FC = () => {
  const { data, loading, error } = useDeploymentMap();

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <InlineAlert severity="error">{error}</InlineAlert>;
  }

  if (!data || data.customers.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No deployments found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {data.customers.length} customers, {data.products.length} products
      </Typography>
      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: 'calc(90vh - 280px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 160, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 3 }}>
                  Customer
                </TableCell>
                {data.products.map((productId) => (
                  <TableCell key={productId} sx={{ fontWeight: 'bold', minWidth: 120 }}>
                    {productId}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.customers.map((customer) => (
                <TableRow key={customer.customer_id} hover>
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    {customer.name}
                  </TableCell>
                  {data.products.map((productId) => {
                    const version = customer.deployments[productId];
                    return (
                      <TableCell key={productId}>
                        {version ? (
                          <Chip
                            label={version}
                            size="small"
                            color={getVersionColor(version)}
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">--</Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DeploymentMapPanel;
